import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/app/providers/AuthProvider';
import { usePermissions } from '@/hooks/usePermissions';
import { useLiveChatStore } from '../store/liveChatStore';
import { type Message } from '../types/liveChat.types';
import { useChatbotId } from '@/hooks/useChatbotId';
import { cookieService } from '@/services/cookie.service';
import toast from 'react-hot-toast';

const getSocketURL = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  return apiUrl.replace(/\/api\/?$/, '');
};

const SOCKET_URL = getSocketURL();

interface GlobalWebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const GlobalWebSocketContext = createContext<GlobalWebSocketContextType>({
  socket: null,
  isConnected: false,
});

export const useGlobalWebSocketContext = () => useContext(GlobalWebSocketContext);

interface GlobalWebSocketProviderProps {
  children: ReactNode;
}

export function GlobalWebSocketProvider({ children }: GlobalWebSocketProviderProps) {
  const { user } = useAuth();
  const { isOwner, isSuperAdmin } = usePermissions();
  const queryClient = useQueryClient();

  const socketRef = useRef<Socket | null>(null);
  const connectedChatbotIdRef = useRef<string | null>(null);
  const activeChatbotIdRef = useRef<string | null>(null);
  const removedConversationIdsRef = useRef<Set<string>>(new Set());
  const latestGlobalFiltersRef = useRef<any>(null);
  const latestStaffAgentIdRef = useRef<string | null>(null);
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const {
    updateMessageStatus,
    updateMessage,
    addMessage,
    updateGlobalConversationStatus,
    fetchGlobalConversations,
    addOrUpdateGlobalConversation,
    removeConversationFromLists,
    updateAgentStatus,
    chatBotId,
    globalFilters,
  } = useLiveChatStore();


  const resolvedChatbotId = useChatbotId();
  const activeChatbotId = resolvedChatbotId || globalFilters.chatBotId || chatBotId || null;

  const staffAgentId = (isOwner || isSuperAdmin)
    ? null
    : ((user as any)?.user?._id as string | null | undefined) ?? null;

  const isConversationSuppressed = useCallback((conversationId: string) => {
    return removedConversationIdsRef.current.has(conversationId);
  }, []);

  const selectedAgentMatchesConversation = useCallback(
    (conversation: any, selectedAgentId: string | null) => {
      if (!selectedAgentId) return true;
      if (selectedAgentId === 'unassigned') return !conversation.assignedAgentId;
      return conversation.assignedAgentId === selectedAgentId;
    },
    []
  );

  const selectedTypeMatchesConversation = useCallback(
    (conversation: any, selectedType?: number | 'all') => {
      if (!selectedType || selectedType === 'all') return true;
      return conversation.type === selectedType;
    },
    []
  );

  const selectedReadStateMatchesConversation = useCallback(
    (conversation: any, selectedReadState?: 'all' | 'read' | 'unread', isActive?: boolean) => {
      if (isActive) return true;
      if (!selectedReadState || selectedReadState === 'all') return true;
      if (selectedReadState === 'read') return (conversation.unreadCount || 0) === 0;
      return (conversation.unreadCount || 0) > 0;
    },
    []
  );

  useEffect(() => {
    latestGlobalFiltersRef.current = globalFilters;
  }, [globalFilters]);

  useEffect(() => {
    activeChatbotIdRef.current = activeChatbotId;
  }, [activeChatbotId]);

  useEffect(() => {
    latestStaffAgentIdRef.current = staffAgentId;
  }, [staffAgentId]);

  useEffect(() => {
    if (!user) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      connectedChatbotIdRef.current = null;
      setIsSocketConnected(false);
      return;
    }

    if (socketRef.current) {
      return;
    }

    const token = cookieService.getCookie('accessToken');

    const socket = io(SOCKET_URL, {
      auth: { token: token || undefined },
      reconnectionDelay: 1000,
      reconnection: true,
      reconnectionAttempts: 10,
      transports: ['websocket', 'polling'],
      autoConnect: true,
      withCredentials: true,
      timeout: 20000,
      path: '/socket.io/',
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      const currentChatbotId = activeChatbotIdRef.current;
      setIsSocketConnected(true);
      if (currentChatbotId) {
        socket.emit('join:chatbot', currentChatbotId);
        connectedChatbotIdRef.current = currentChatbotId;
      }
    });

    socket.on('disconnect', () => {
      setIsSocketConnected(false);
    });

    socket.on('connect_error', (err) => {
      console.error('[GlobalWebSocket] Connection error:', err.message);
      setIsSocketConnected(false);
    });

    socket.on('reconnect', () => {
      const currentChatbotId = activeChatbotIdRef.current;
      setIsSocketConnected(true);
      if (currentChatbotId) {
        socket.emit('join:chatbot', currentChatbotId);
        connectedChatbotIdRef.current = currentChatbotId;
      }
      fetchGlobalConversations();
    });

    socket.on('message:new', (message: Message) => {
      if (!message?.conversationId) return;
      const currentChatbotId = activeChatbotIdRef.current;
      if (currentChatbotId && message.chatBotId && message.chatBotId !== currentChatbotId) return;

      addMessage(message.conversationId, {
        ...message,
        createdAt: new Date(message.createdAt),
      });
    });

    socket.on('message:update', (message: Message) => {
      if (!message?.conversationId) return;
      const currentChatbotId = activeChatbotIdRef.current;
      if (currentChatbotId && message.chatBotId && message.chatBotId !== currentChatbotId) return;

      updateMessage(message.conversationId, message);
    });

    socket.on('message:workflow', (data: any) => {
      if (!data?.conversationId) return;
      const currentChatbotId = activeChatbotIdRef.current;
      if (currentChatbotId && data.chatBotId && data.chatBotId !== currentChatbotId) return;

      addMessage(data.conversationId, {
        ...data,
        createdAt: new Date(data.createdAt),
      });
    });

    socket.on('message:bot:chunk', () => {});
    socket.on('message:processing', () => {});
    socket.on('message:complete', () => {});
    socket.on('message:error', () => {});

    socket.on('conversation:new', (conversation: any) => {
      const activeStaffAgentId = latestStaffAgentIdRef.current;
      const currentGlobalFilters = latestGlobalFiltersRef.current || {};
      const currentChatbotId = activeChatbotIdRef.current;
      const activeConversationId = useLiveChatStore.getState().activeConversationId;
      const isActive = conversation?._id === activeConversationId;

      if (currentChatbotId && conversation?.chatBotId && conversation.chatBotId !== currentChatbotId) {
        return;
      }

      if (activeStaffAgentId && conversation?.assignedAgentId !== activeStaffAgentId) {
        return;
      }

      if (!activeStaffAgentId && !selectedAgentMatchesConversation(conversation, currentGlobalFilters.agentId || null)) {
        return;
      }

      if (!selectedTypeMatchesConversation(conversation, currentGlobalFilters.conversationType)) {
        return;
      }

      if (!selectedReadStateMatchesConversation(conversation, currentGlobalFilters.readState, isActive)) {
        return;
      }

      if (conversation && conversation._id) {
        removedConversationIdsRef.current.delete(conversation._id);
        addOrUpdateGlobalConversation(conversation);
        return;
      }

      fetchGlobalConversations();
    });

    socket.on('conversation:update', (data: any) => {
      const conversation = data.conversation || data;
      const counts = data.counts;
      if (!conversation?._id) return;

      const activeChatbotId = connectedChatbotIdRef.current;
      if (activeChatbotId && conversation.chatBotId && conversation.chatBotId !== activeChatbotId) return;

      const activeStaffAgentId = latestStaffAgentIdRef.current;
      const currentGlobalFilters = latestGlobalFiltersRef.current || {};
      const selectedAgentId = currentGlobalFilters.agentId || null;
      const selectedConversationType = currentGlobalFilters.conversationType;
      const selectedReadState = currentGlobalFilters.readState;
      const activeConversationId = useLiveChatStore.getState().activeConversationId;
      const isActive = conversation?._id === activeConversationId;
      const conversationIsSuppressed = isConversationSuppressed(conversation._id);

      if (conversationIsSuppressed) {
        const shouldRestore =
          (activeStaffAgentId && conversation.assignedAgentId === activeStaffAgentId) ||
          (selectedAgentMatchesConversation(conversation, selectedAgentId) &&
            selectedTypeMatchesConversation(conversation, selectedConversationType) &&
            selectedReadStateMatchesConversation(conversation, selectedReadState, isActive));

        if (!shouldRestore) {
          return;
        }

        removedConversationIdsRef.current.delete(conversation._id);
      }

      if (activeStaffAgentId) {
        if (conversation.assignedAgentId === activeStaffAgentId) {
          addOrUpdateGlobalConversation(conversation, counts);
        } else {
          useLiveChatStore.setState((state) => ({
            conversations: state.conversations.filter((c) => c._id !== conversation._id),
            globalConversations: state.globalConversations.filter((c) => c._id !== conversation._id),
          }));
        }
      } else if (selectedAgentId) {
        if (
          selectedAgentMatchesConversation(conversation, selectedAgentId) &&
          selectedTypeMatchesConversation(conversation, selectedConversationType) &&
          selectedReadStateMatchesConversation(conversation, selectedReadState, isActive)
        ) {
          addOrUpdateGlobalConversation(conversation, counts);
        } else {
          useLiveChatStore.setState((state) => ({
            conversations: state.conversations.filter((c) => c._id !== conversation._id),
            globalConversations: state.globalConversations.filter((c) => c._id !== conversation._id),
          }));
        }
      } else {
        addOrUpdateGlobalConversation(conversation, counts);
      }
    });

    socket.on('conversation:status', (data: any) => {
      const { conversationId, status, counts } = data;
      updateGlobalConversationStatus(conversationId, status, counts);
    });

    socket.on(
      'agent:status:update',
      ({ agentId, status }: { agentId: string; status: 'online' | 'offline' }) => {
        if (!agentId) return;
        updateAgentStatus(agentId, status);
      }
    );

    socket.on(
      'chatbot:unread-counts:update',
      ({
        unreadConversationCountsByChatbot,
      }: {
        unreadConversationCountsByChatbot: Array<{ chatBotId: string; unreadConversationCount: number }>;
      }) => {
        queryClient.setQueryData(['chatbotList'], (oldData: any) => {
          if (!oldData?.data?.botList) return oldData;

          const updatedBotList = oldData.data.botList.map((bot: any) => {
            const updatedCount = unreadConversationCountsByChatbot.find(
              (item) => item.chatBotId === bot._id
            );

            return updatedCount
              ? { ...bot, unreadConversationCount: updatedCount.unreadConversationCount }
              : bot;
          });

          return {
            ...oldData,
            data: {
              ...oldData.data,
              botList: updatedBotList,
            },
          };
        });
      }
    );

    socket.on('conversation:assigned', ({ conversation }: { conversation: any }) => {
      const currentChatbotId = activeChatbotIdRef.current;
      if (currentChatbotId && conversation?.chatBotId && conversation.chatBotId !== currentChatbotId) return;
      if (!conversation?._id) return;

      removedConversationIdsRef.current.delete(conversation._id);
      addOrUpdateGlobalConversation(conversation);
    });

    socket.on(
      'conversation:unassigned',
      ({ conversation }: { conversation: any; assignedById?: string }) => {
        const currentChatbotId = activeChatbotIdRef.current;
        if (currentChatbotId && conversation?.chatBotId && conversation.chatBotId !== currentChatbotId) return;
        if (!conversation?._id) return;

        removeConversationFromLists(conversation._id);
      }
    );

    socket.on(
      'conversation:removed',
      ({
        conversationId,
      }: {
        conversationId: string;
        assignedById?: string;
        reason?: 'unassigned' | 'transferred';
      }) => {
        if (!conversationId) return;

        removedConversationIdsRef.current.add(conversationId);
        removeConversationFromLists(conversationId);
      }
    );

    socket.on('conversation:cleared', ({ conversationId }: { conversationId: string }) => {
      if (!conversationId) return;
      useLiveChatStore.setState((state) => ({
        messages: {
          ...state.messages,
          [conversationId]: [],
        },
      }));
    });

    const seenNotifIds = new Set<string>();

    socket.on('notification:new', (notif: { _id?: string; title?: string; message?: string; link?: string }) => {
      if (notif._id) {
        if (seenNotifIds.has(notif._id)) return;
        seenNotifIds.add(notif._id);
        if (seenNotifIds.size > 100) {
          const first = seenNotifIds.values().next().value;
          if (first) seenNotifIds.delete(first);
        }
      }

      toast(
        () => (
          <div className="flex flex-col gap-0.5">
            <span className="font-bold text-xs text-gray-900">{notif.title || '🔔 Notification'}</span>
            <span className="text-xs text-gray-600 leading-snug">{notif.message}</span>
          </div>
        ),
        { icon: '🔔', duration: 6000 }
      );
      queryClient.invalidateQueries({ queryKey: ['global-notifications'] });
      queryClient.invalidateQueries({ queryKey: ['global-notifications-infinite'] });
    });

    return () => {
      socket.disconnect();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
      connectedChatbotIdRef.current = null;
      setIsSocketConnected(false);
    };
  }, [
    user,
    fetchGlobalConversations,
    addMessage,
    updateMessageStatus,
    updateGlobalConversationStatus,
    addOrUpdateGlobalConversation,
    removeConversationFromLists,
    updateAgentStatus,
    isConversationSuppressed,
    selectedAgentMatchesConversation,
    selectedTypeMatchesConversation,
    selectedReadStateMatchesConversation,
    queryClient,
  ]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !isSocketConnected || !activeChatbotId) return;
    if (connectedChatbotIdRef.current === activeChatbotId) return;

    socket.emit('join:chatbot', activeChatbotId);
    connectedChatbotIdRef.current = activeChatbotId;
  }, [activeChatbotId, isSocketConnected]);

  return (
    <GlobalWebSocketContext.Provider
      value={{
        socket: socketRef.current,
        isConnected: isSocketConnected,
      }}
    >
      {children}
    </GlobalWebSocketContext.Provider>
  );
}
